import { useCallback, useEffect, useState } from 'react';
import type { Account, AdminUser, IssuedLink, MailerInfo, Me, PendingInvite } from '@momomail/shared';
import { bridge, errorText } from '../lib/bridge';
import { timeAgo } from '../lib/format';
import { t } from '../lib/i18n';
import { copyText, formatTime, people } from '../lib/people';
import { useGoogleInfo } from './Google';
import { Icon } from './Icon';
import { confirmDialog, Dialog } from './Overlay';
import { usePasswordPair } from './Public';
import { toast } from './Toasts';

/** The recovery email and change-password rows under "账号与连接" (Account & connection) in Settings, and "用户管理" (User management) for multi-user servers. */

/** Who is logged in; local = true in desktop local mode. null when an old backend has no /api/me. */
export function useMe(open: boolean): Me | null {
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    if (!open) return;
    people.me().then(setMe, () => setMe(null));
  }, [open]);
  return me;
}

function Row({ title, desc, children }: { title: string; desc?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="setting">
      <div className="setting__text">
        <div className="setting__title">{title}</div>
        {desc && <div className="setting__desc muted">{desc}</div>}
      </div>
      {children && <div className="setting__control">{children}</div>}
    </div>
  );
}

// ---------------- My account (lives under Settings → Account & connection) ----------------

/** Recovery email: normally one line showing the current value; the input only expands after clicking "设置 / 修改" (Set / Change). */
export function RecoveryEmailRow({ me, onMe }: { me: Me; onMe: (m: Me) => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(me.recoveryEmail ?? '');
  const [saving, setSaving] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      onMe(await people.updateMe({ recoveryEmail: email.trim() || null }));
      toast(t('找回邮箱已保存'), 'ok');
      setOpen(false);
    } catch (err) {
      toast(errorText(err), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <Row
        title={t('找回邮箱')}
        desc={me.recoveryEmail ? t('忘了密码时，重置链接发到 {email}', { email: me.recoveryEmail }) : t('还没有设置：忘了密码时没法自己找回')}
      >
        {!open && (
          <button className="btn btn--small" onClick={() => (setEmail(me.recoveryEmail ?? ''), setOpen(true))}>
            <Icon name="mail" size={14} /> {me.recoveryEmail ? t('修改') : t('设置')}
          </button>
        )}
      </Row>
      {open && (
        <form className="conn-form setting-expand" onSubmit={(e) => void save(e)}>
          <p className="muted setting__desc">{t('请填一个你平时能收到信的邮箱。')}</p>
          <div className="user-inline">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" aria-label={t('找回邮箱')} autoFocus />
            <button className="btn btn--small btn--primary" type="submit" disabled={saving || email.trim() === (me.recoveryEmail ?? '')}>
              {saving ? <span className="spinner" /> : <Icon name="check" size={14} />} {t('保存')}
            </button>
            <button className="btn btn--small btn--quiet" type="button" onClick={() => setOpen(false)}>
              {t('取消')}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

/** Change password: normally one line; the form only expands after clicking "修改密码" (Change password). */
export function PasswordRow() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const pair = usePasswordPair();
  const [busy, setBusy] = useState(false);
  const close = () => {
    setOpen(false);
    setCurrent('');
    pair.reset();
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await people.changePassword(current, pair.pw);
      close();
      toast(t('密码已修改，其他设备上的登录已经退出'), 'ok');
    } catch (err) {
      toast(errorText(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Row title={t('密码')} desc={t('改好以后，其他设备（包括手机 App）上的登录都会退出，这台设备保持登录。')}>
        {!open && (
          <button className="btn btn--small" onClick={() => setOpen(true)}>
            <Icon name="lock" size={14} /> {t('修改密码')}
          </button>
        )}
      </Row>
      {open && (
        <form className="conn-form setting-expand" onSubmit={(e) => void submit(e)}>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('当前密码')}</span>
              <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required autoFocus />
            </label>
            <span />
            {pair.fields()}
          </div>
          <div className="btn-row">
            <button className="btn btn--small btn--primary" type="submit" disabled={busy || !current || !pair.ready}>
              {busy ? <span className="spinner" /> : <Icon name="lock" size={14} />} {t('修改密码')}
            </button>
            <button className="btn btn--small btn--quiet" type="button" onClick={close}>
              {t('取消')}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

// ---------------- User management ----------------

/** A freshly generated invite / reset link: shown only this once. */
function LinkDialog({ link, onClose }: { link: { title: string; text: string; issued: IssuedLink } | null; onClose: () => void }) {
  const copy = async () => {
    if (link && (await copyText(link.issued.url))) toast(t('已复制链接'), 'ok');
    else toast(t('复制失败，请手动选中链接复制'), 'error');
  };
  return (
    <Dialog
      open={!!link}
      onClose={onClose}
      title={link?.title ?? ''}
      labelledBy="link-title"
      className="dialog__card--small"
      footer={
        <>
          <button className="btn btn--quiet" onClick={onClose}>
            {t('关闭')}
          </button>
          <button className="btn btn--primary" data-autofocus onClick={() => void copy()}>
            <Icon name="copy" size={15} /> {t('复制链接')}
          </button>
        </>
      }
    >
      {link && (
        <div className="link-box">
          <p>{link.text}</p>
          <input className="link-box__url" readOnly value={link.issued.url} onFocus={(e) => e.target.select()} aria-label={t('链接')} />
          <p className="muted">{t('{time} 前有效，只能用一次。这个链接只显示这一次，关掉后就看不到了。', { time: formatTime(link.issued.expiresAt) })}</p>
        </div>
      )}
    </Dialog>
  );
}

export function UserAdmin({ me, accounts }: { me: Me; accounts: Account[] }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [mailer, setMailer] = useState<MailerInfo | null>(null);
  const [selfReset, setSelfReset] = useState(false);
  const [note, setNote] = useState('');
  const [asAdmin, setAsAdmin] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [link, setLink] = useState<{ title: string; text: string; issued: IssuedLink } | null>(null);

  const reload = useCallback(async () => {
    try {
      const [u, i, m, h] = await Promise.all([people.users(), people.invites(), people.mailer(), people.health()]);
      setUsers(u);
      setInvites(i);
      setMailer(m);
      setSelfReset(h.selfReset);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);

  /** Run an action: spinner, error toast, then reload everything. */
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast(ok, 'ok');
    } catch (err) {
      toast(errorText(err), 'error');
    } finally {
      setBusy(null);
      await reload();
    }
  };

  const invite = () =>
    run('invite', async () => {
      const issued = await people.invite(asAdmin, note.trim());
      setNote('');
      setAsAdmin(false);
      setLink({
        title: asAdmin ? t('管理员邀请链接') : t('邀请链接'),
        text: note.trim() ? t('把这个链接发给{name}。对方打开后自己设用户名、密码和找回邮箱，你看不到他的密码。', { name: note.trim() }) : t('把这个链接发给对方。对方打开后自己设用户名、密码和找回邮箱，你看不到他的密码。'),
        issued,
      });
    });

  const resetLink = (u: AdminUser) =>
    run(`reset-${u.id}`, async () => {
      const issued = await people.resetLink(u.id);
      setLink({ title: t('{name} 的重置密码链接', { name: u.username }), text: t('把这个链接交给 {name}，他打开后自己设新密码——你看不到新密码。设好后他所有设备上的登录都会退出。', { name: u.username }), issued });
    });

  const remove = async (u: AdminUser) => {
    const choice = await confirmDialog({
      title: t('删除用户"{name}"？', { name: u.username }),
      message: (
        <>
          {t('他的 {n} 个邮箱账户、邮件缓存、手机推送登记会全部删掉，并退出所有设备。', { n: u.accounts })}
          <b>{t('邮箱服务器上的邮件不受影响')}</b>
          {t('。这一步不能撤销。')}
        </>
      ),
      confirm: t('删除用户'),
      danger: true,
    });
    if (choice === 'confirm') await run(`del-${u.id}`, () => people.removeUser(u.id), t('已删除 {name}', { name: u.username }));
  };

  const toggleDisabled = async (u: AdminUser) => {
    if (!u.disabled) {
      const choice = await confirmDialog({ title: t('停用"{name}"？', { name: u.username }), message: t('他会退出所有设备、登不上，服务器也停止替他收信。数据都留着，随时可以恢复。'), confirm: t('停用'), danger: true });
      if (choice !== 'confirm') return;
    }
    await run(`dis-${u.id}`, () => people.updateUser(u.id, { disabled: !u.disabled }), u.disabled ? t('已恢复 {name}', { name: u.username }) : t('已停用 {name}', { name: u.username }));
  };

  const mailerAccountMissing = mailer?.mailer && !accounts.some((a) => a.id === mailer.mailer!.accountId);

  return (
    <div className="settings__section">
      <h3 className="settings__h">{t('用户管理')}</h3>

      {/* System sender mailbox goes first: without it, new users who forget their password can't recover it themselves, and invites can't be sent */}
      <Row
        title={t('系统发信邮箱')}
        desc={
          <>
            {t('"忘记密码"的重置邮件从这个邮箱发出。只能选你自己名下的邮箱账户。')}
            {mailerAccountMissing && t(' 现在用的是 {user} 的 {email}。', { user: mailer!.mailer!.username, email: mailer!.mailer!.email })}
          </>
        }
      >
        <select
          className="setting__select"
          value={mailer?.mailer && !mailerAccountMissing ? mailer.mailer.accountId : ''}
          disabled={busy === 'mailer' || !mailer}
          onChange={(e) => void run('mailer', () => people.setMailer(e.target.value || null), e.target.value ? t('已设置系统发信邮箱') : t('已取消系统发信邮箱'))}
          aria-label={t('系统发信邮箱')}
        >
          <option value="">{mailerAccountMissing ? `${mailer!.mailer!.email} (${mailer!.mailer!.username})` : t('不设置')}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.email}
            </option>
          ))}
        </select>
      </Row>
      {mailer && mailer.publicUrl == null && (
        <div className="notice notice--warn">
          <Icon name="alert" size={14} /> {t('服务器还需要配置')} <code>MOMOMAIL_PUBLIC_URL</code>
          {t('（比如 https://mail.example.com），才能发重置邮件、生成邀请链接。')}
        </div>
      )}
      {accounts.length === 0 && <p className="muted">{t('你还没有邮箱账户：先在"账户"里加一个，才能选它当系统发信邮箱。')}</p>}

      <GoogleAdminRow />

      <div className="conn-form">
        <p className="setting__title">{t('邀请新用户')}</p>
        <p className="muted setting__desc">{t('生成一个一次性链接（7 天有效）发给对方，对方自己设用户名、密码和找回邮箱。服务器不开放注册。')}</p>
        {!selfReset && (
          <div className="notice notice--warn">
            <Icon name="alert" size={14} /> {t('先设置系统发信邮箱，新用户忘了密码才能自己找回。')}
            {mailer?.publicUrl == null && t(' 服务器还需要配置 MOMOMAIL_PUBLIC_URL。')}
          </div>
        )}
        <div className="user-inline">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('备注（可以不填，比如：妈妈）')} aria-label={t('备注')} maxLength={200} />
          <label className="user-check">
            <input type="checkbox" checked={asAdmin} onChange={(e) => setAsAdmin(e.target.checked)} /> {t('管理员')}
          </label>
          <button className="btn btn--small btn--primary" onClick={() => void invite()} disabled={!selfReset || busy === 'invite'}>
            {busy === 'invite' ? <span className="spinner" /> : <Icon name="plus" size={14} />} {t('生成邀请链接')}
          </button>
        </div>
        {invites.length > 0 && (
          <ul className="account-cards">
            {invites.map((i) => (
              <li key={i.id} className="account-card user-card">
                <Icon name="mail" size={18} />
                <div className="account-card__text">
                  <strong>{i.note || t('未备注的邀请')}</strong>
                  <span className="muted">
                    {i.isAdmin && <span className="tag">{t('管理员')}</span>} {t('{time} 前有效，还没人用', { time: formatTime(i.expiresAt) })}
                  </span>
                </div>
                <button className="btn btn--small btn--quiet" disabled={busy === `inv-${i.id}`} onClick={() => void run(`inv-${i.id}`, () => people.revokeInvite(i.id), t('已撤销邀请'))}>
                  {t('撤销')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="conn-form">
        <p className="setting__title">{t('用户')}</p>
        {!users ? (
          <span className="spinner" />
        ) : (
          <ul className="account-cards">
            {users.map((u) => {
              const self = u.id === me.id;
              return (
                <li key={u.id} className={`account-card user-card ${u.disabled ? 'is-disabled' : ''}`}>
                  <Icon name="user" size={18} />
                  <div className="account-card__text">
                    <strong>
                      {u.username}
                      {self && <span className="muted">{t('（你）')}</span>}
                    </strong>
                    <span className="user-card__tags">
                      {u.isAdmin && <span className="tag">{t('管理员')}</span>}
                      {u.disabled && <span className="tag tag--dim">{t('已停用')}</span>}
                      <span className="muted">
                        {t('{n} 个邮箱账户', { n: u.accounts })} · {u.lastLogin ? t('{ago}登录', { ago: timeAgo(u.lastLogin) }) : t('还没登录过')} · {u.recoveryEmail ? t('有找回邮箱') : t('没有找回邮箱')}
                      </span>
                    </span>
                  </div>
                  <div className="user-card__actions">
                    <button className="btn btn--small btn--quiet" disabled={!!busy} onClick={() => void resetLink(u)} title={t('生成重置密码链接')}>
                      {t('重置链接')}
                    </button>
                    {!self && (
                      <>
                        <button
                          className="btn btn--small btn--quiet"
                          disabled={!!busy}
                          onClick={() => void run(`adm-${u.id}`, () => people.updateUser(u.id, { isAdmin: !u.isAdmin }), u.isAdmin ? t('已取消 {name} 的管理员', { name: u.username }) : t('{name} 现在是管理员', { name: u.username }))}
                        >
                          {u.isAdmin ? t('取消管理员') : t('设为管理员')}
                        </button>
                        <button className="btn btn--small btn--quiet" disabled={!!busy} onClick={() => void toggleDisabled(u)}>
                          {u.disabled ? t('恢复') : t('停用')}
                        </button>
                        <button className="btn btn--small btn--quiet tool--danger" disabled={!!busy} onClick={() => void remove(u)} aria-label={t('删除用户 {name}', { name: u.username })}>
                          <Icon name="trash" size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <LinkDialog link={link} onClose={() => setLink(null)} />
    </div>
  );
}

/** For admins: whether the server has Google sign-in configured, and which callback URL to register in Google Cloud. */
function GoogleAdminRow() {
  const info = useGoogleInfo();
  if (!info) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.redirectUri ?? '');
      toast(t('已复制'), 'ok');
    } catch {
      toast(t('复制失败，请手动选中复制'), 'error');
    }
  };
  return (
    <Row
      title={t('Google 登录')}
      desc={
        info.google ? (
          <>
            {t('已配置：用户可以用 Google 登录 Gmail、连接 Google 日历。Google Cloud 的 OAuth 客户端里要登记这个回调地址：')}
            <br />
            <code className="conn-url">{info.redirectUri}</code>
          </>
        ) : (
          <>{t('没有配置：Gmail 只能用应用专用密码，"加入日历"只能打开 Google 日历网页或下载 .ics。配置方法见 deploy/README.md（Google Cloud 建一个 OAuth 客户端，填 MOMOMAIL_GOOGLE_CLIENT_ID / SECRET）。')}</>
        )
      }
    >
      {info.google ? (
        <button className="btn btn--small btn--quiet" onClick={() => void copy()}>
          <Icon name="copy" size={14} /> {t('复制回调地址')}
        </button>
      ) : (
        <span className="tag tag--dim">{t('未配置')}</span>
      )}
    </Row>
  );
}
